import { useState, useEffect, useCallback } from "react";
import { getLocale } from "../lang/useLanguage";

const API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:8001" : `${window.location.protocol}//${window.location.hostname}/api`;
const fmt = (d) => d ? new Date(d).toLocaleDateString(getLocale(), { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";

const STATUS_CFG = {
  draft:       { label: "Taslak",     color: "#94a3b8" },
  planned:     { label: "Planlandı",  color: "#60a5fa" },
  released:    { label: "Serbest",    color: "#a78bfa" },
  in_progress: { label: "Üretimde",   color: "#fbbf24" },
  completed:   { label: "Tamamlandı", color: "#00ff9d" },
};

const PRIORITY_CFG = {
  low:      { label: "Düşük",  color: "#64748b" },
  normal:   { label: "Normal", color: "#60a5fa" },
  high:     { label: "Yüksek", color: "#fbbf24" },
  critical: { label: "Kritik", color: "#f87171" },
};

// ─── LOGIN EKRANI ─────────────────────────────────────────────────────────
const KioskLogin = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async () => {
    if (!username || !password) { setError("Kullanıcı adı ve şifre gerekli."); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: `username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}`,
      });
      if (!res.ok) throw new Error("Kullanıcı adı veya şifre hatalı");
      const data = await res.json();
      onLogin({ token: data.access_token, username, role: data.role || "operator" });
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const handleKeyDown = (e) => { if (e.key === "Enter") handleLogin(); };

  return (
    <div style={{ minHeight: "100vh", background: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <div style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 24, padding: 48, width: "100%", maxWidth: 420, boxShadow: "0 25px 50px rgba(0,0,0,0.5)" }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🏭</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: "#f1f5f9", letterSpacing: "-0.02em" }}>Smart Factory</div>
          <div style={{ fontSize: 14, color: "#64748b", marginTop: 6 }}>Operatör Kiosk</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
              Kullanıcı Adı
            </label>
            <input type="text" value={username} onChange={e => setUsername(e.target.value)} onKeyDown={handleKeyDown}
              placeholder="Kullanıcı adınızı girin"
              style={{ width: "100%", padding: "14px 16px", background: "#0f172a", border: "1px solid #334155", borderRadius: 10, color: "#f1f5f9", fontSize: 16, outline: "none", boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
              Şifre
            </label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={handleKeyDown}
              placeholder="Şifrenizi girin"
              style={{ width: "100%", padding: "14px 16px", background: "#0f172a", border: "1px solid #334155", borderRadius: 10, color: "#f1f5f9", fontSize: 16, outline: "none", boxSizing: "border-box" }} />
          </div>
          {error && (
            <div style={{ padding: "12px 16px", background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.3)", borderRadius: 8, color: "#f87171", fontSize: 14 }}>
              ⚠️ {error}
            </div>
          )}
          <button onClick={handleLogin} disabled={loading} style={{
            padding: "16px", borderRadius: 12, border: "none",
            background: loading ? "#334155" : "linear-gradient(135deg,#3b82f6,#6366f1)",
            color: "#fff", cursor: loading ? "default" : "pointer",
            fontSize: 16, fontWeight: 700, marginTop: 8,
            boxShadow: loading ? "none" : "0 4px 20px rgba(59,130,246,0.4)",
          }}>
            {loading ? "Giriş yapılıyor..." : "🔐 Giriş Yap"}
          </button>
        </div>
        <div style={{ textAlign: "center", marginTop: 24, fontSize: 12, color: "#475569" }}>
          Smart Factory MES · Operatör Ekranı
        </div>
      </div>
    </div>
  );
};

// ─── PROBLEM BİLDİRİM MODAL ───────────────────────────────────────────────
const ProblemModal = ({ order, onClose, onSubmit }) => {
  const [problem, setProblem] = useState("");
  const [type, setType] = useState("machine");

  const problems = [
    { id: "machine", label: "🔧 Makine Arızası", desc: "Makine çalışmıyor veya arızalı" },
    { id: "material", label: "📦 Malzeme Eksik", desc: "Üretim için malzeme yok" },
    { id: "drawing", label: "📐 Teknik Sorun", desc: "Çizim veya ölçü sorunu" },
    { id: "quality", label: "⚠️ Kalite Sorunu", desc: "Ürün kalite standardını karşılamıyor" },
    { id: "other", label: "❓ Diğer", desc: "Başka bir sorun" },
  ];

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.8)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={onClose}>
      <div style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 20, width: "100%", maxWidth: 500, padding: 32 }}
        onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 22, fontWeight: 800, color: "#f1f5f9", marginBottom: 8 }}>⚠️ Sorun Bildir</div>
        <div style={{ fontSize: 14, color: "#64748b", marginBottom: 24 }}>İş Emri: {order.order_no}</div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
          {problems.map(p => (
            <button key={p.id} onClick={() => setType(p.id)} style={{
              padding: "14px 16px", borderRadius: 12, cursor: "pointer", textAlign: "left",
              border: `2px solid ${type === p.id ? "#f87171" : "#334155"}`,
              background: type === p.id ? "rgba(248,113,113,0.1)" : "#0f172a",
              transition: "all 0.15s",
            }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: type === p.id ? "#f87171" : "#94a3b8" }}>{p.label}</div>
              <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>{p.desc}</div>
            </button>
          ))}
        </div>

        <textarea value={problem} onChange={e => setProblem(e.target.value)}
          placeholder="Sorunu daha detaylı açıklayın (isteğe bağlı)..."
          rows={3} style={{ width: "100%", padding: "12px 14px", background: "#0f172a", border: "1px solid #334155",
            borderRadius: 10, color: "#f1f5f9", fontSize: 14, outline: "none", resize: "vertical",
            boxSizing: "border-box", marginBottom: 20 }} />

        <div style={{ display: "flex", gap: 12 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "14px", borderRadius: 10, border: "1px solid #334155",
            background: "none", color: "#94a3b8", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>İptal</button>
          <button onClick={() => onSubmit(type, problem)} style={{ flex: 2, padding: "14px", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,#ef4444,#f87171)", color: "#fff", cursor: "pointer",
            fontSize: 14, fontWeight: 700 }}>🚨 Bildiri Gönder</button>
        </div>
      </div>
    </div>
  );
};

// ─── İŞ EMRİ DETAY ────────────────────────────────────────────────────────
const OrderDetail = ({ order, token, onBack, onRefresh }) => {
  const [materials, setMaterials] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [showProblem, setShowProblem] = useState(false);
  const [actionResult, setActionResult] = useState(null);

  const hdrs = useCallback(
    () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
    [token]
  );

  useEffect(() => {
    fetch(`${API}/work-orders/${order.id}/material-check`, { headers: hdrs() })
      .then(r => r.ok ? r.json() : [])
      .then(d => setMaterials(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [order.id, hdrs]);

  const doAction = async (action, notes = "") => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/kiosk/orders/${order.id}/action`, {
        method: "PATCH", headers: hdrs(),
        body: JSON.stringify({ action, notes }),
      });
      if (res.ok) {
        setActionResult(action);
        onRefresh();
        if (action === "complete") {
          setTimeout(() => onBack(), 2000);
        }
      }
    } catch {}
    setActionLoading(false);
  };

  const pct = order.completion_pct ?? 0;
  const statusCfg = STATUS_CFG[order.status] || STATUS_CFG.released;
  const priorityCfg = PRIORITY_CFG[order.priority] || PRIORITY_CFG.normal;

  return (
    <div style={{ minHeight: "100vh", background: "#0f172a", fontFamily: "'DM Sans','Segoe UI',sans-serif", color: "#f1f5f9" }}>

      {/* Header */}
      <div style={{ background: "#1e293b", borderBottom: "1px solid #334155", padding: "16px 24px", display: "flex", alignItems: "center", gap: 16 }}>
        <button onClick={onBack} style={{ padding: "10px 16px", borderRadius: 10, border: "1px solid #334155", background: "none", color: "#94a3b8", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>
          ← Geri
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#f1f5f9" }}>{order.order_no}</div>
          <div style={{ fontSize: 13, color: "#64748b" }}>{order.product_code} · {order.customer || "—"}</div>
        </div>
        <span style={{ padding: "6px 14px", borderRadius: 20, fontSize: 13, fontWeight: 700,
          color: statusCfg.color, background: `${statusCfg.color}18`, border: `1px solid ${statusCfg.color}44` }}>
          {statusCfg.label}
        </span>
        <span style={{ padding: "6px 14px", borderRadius: 20, fontSize: 13, fontWeight: 700,
          color: priorityCfg.color, background: `${priorityCfg.color}18`, border: `1px solid ${priorityCfg.color}44` }}>
          {priorityCfg.label}
        </span>
      </div>

      <div style={{ padding: 24, maxWidth: 900, margin: "0 auto" }}>

        {/* Tamamlanma */}
        <div style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 16, padding: 24, marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#f1f5f9" }}>📊 Üretim İlerlemesi</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#00ff9d" }}>{pct}%</div>
          </div>
          <div style={{ height: 16, background: "#0f172a", borderRadius: 8, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${pct}%`, background: "linear-gradient(90deg,#00ff9d88,#00ff9d)", borderRadius: 8, transition: "width 0.6s ease" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 13, color: "#64748b" }}>
            <span>Üretilen: {order.completed_quantity || 0} adet</span>
            <span>Toplam: {order.quantity} adet</span>
          </div>
        </div>

        {/* Bilgiler */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
          <div style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 16, padding: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#64748b", marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.06em" }}>📋 İş Emri Bilgileri</div>
            {[
              ["Ürün Kodu", order.product_code],
              ["Müşteri", order.customer || "—"],
              ["Miktar", `${order.quantity} adet`],
              ["Makine", order.machine || "—"],
              ["Termin", fmt(order.deadline)],
            ].map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #1e293b", fontSize: 14 }}>
                <span style={{ color: "#64748b" }}>{k}</span>
                <span style={{ color: "#f1f5f9", fontWeight: 600 }}>{v}</span>
              </div>
            ))}
          </div>

          {/* Reçete */}
          <div style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 16, padding: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#64748b", marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.06em" }}>📋 Ürün Reçetesi</div>
            {materials.length === 0 ? (
              <div style={{ textAlign: "center", color: "#475569", padding: "20px 0", fontSize: 14 }}>Reçete tanımlanmamış</div>
            ) : materials.map((m, i) => {
              const ok = m.available >= m.required;
              return (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #0f172a", fontSize: 14 }}>
                  <span style={{ color: "#94a3b8" }}>{m.name}</span>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ color: "#64748b", fontSize: 12 }}>{m.required} {m.unit}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: ok ? "#00ff9d" : "#f87171" }}>
                      {ok ? "✓" : "✗"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Notlar */}
        {order.notes && (
          <div style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 16, padding: 20, marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#64748b", marginBottom: 8, textTransform: "uppercase" }}>📝 Notlar</div>
            <div style={{ fontSize: 14, color: "#94a3b8", lineHeight: 1.6 }}>{order.notes}</div>
          </div>
        )}

        {/* Aksiyon Sonucu */}
        {actionResult && (
          <div style={{ padding: "16px 20px", borderRadius: 12, marginBottom: 20, fontWeight: 700, fontSize: 16,
            background: actionResult === "complete" ? "rgba(0,255,157,0.1)" : actionResult === "problem" ? "rgba(248,113,113,0.1)" : "rgba(96,165,250,0.1)",
            border: `1px solid ${actionResult === "complete" ? "#00ff9d" : actionResult === "problem" ? "#f87171" : "#60a5fa"}44`,
            color: actionResult === "complete" ? "#00ff9d" : actionResult === "problem" ? "#f87171" : "#60a5fa",
          }}>
            {actionResult === "complete" ? "✅ İş emri tamamlandı! Yönlendiriliyorsunuz..." :
             actionResult === "start" ? "▶ Üretim başlatıldı" :
             actionResult === "pause" ? "⏸ Üretim duraklatıldı" :
             actionResult === "problem" ? "🚨 Sorun bildirildi, bakım ekibi bilgilendirildi" : ""}
          </div>
        )}

        {/* AKSİYON BUTONLARI */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          {order.status !== "in_progress" && order.status !== "completed" && (
            <button onClick={() => doAction("start")} disabled={actionLoading} style={{
              padding: "24px", borderRadius: 16, border: "none", cursor: "pointer",
              background: "linear-gradient(135deg,#059669,#10b981)", color: "#fff",
              fontSize: 18, fontWeight: 800, boxShadow: "0 4px 20px rgba(16,185,129,0.4)",
              opacity: actionLoading ? 0.7 : 1,
            }}>
              ▶ Üretime Başla
            </button>
          )}
          {order.status === "in_progress" && (
            <>
              <button onClick={() => doAction("pause")} disabled={actionLoading} style={{
                padding: "24px", borderRadius: 16, border: "none", cursor: "pointer",
                background: "linear-gradient(135deg,#d97706,#f59e0b)", color: "#fff",
                fontSize: 18, fontWeight: 800, opacity: actionLoading ? 0.7 : 1,
              }}>
                ⏸ Duraklat
              </button>
              <button onClick={() => doAction("complete")} disabled={actionLoading} style={{
                padding: "24px", borderRadius: 16, border: "none", cursor: "pointer",
                background: "linear-gradient(135deg,#2563eb,#3b82f6)", color: "#fff",
                fontSize: 18, fontWeight: 800, boxShadow: "0 4px 20px rgba(59,130,246,0.4)",
                opacity: actionLoading ? 0.7 : 1,
              }}>
                ✅ Tamamla
              </button>
            </>
          )}
          <button onClick={() => setShowProblem(true)} style={{
            padding: "24px", borderRadius: 16, border: "2px solid #f87171",
            background: "rgba(248,113,113,0.1)", color: "#f87171",
            fontSize: 18, fontWeight: 800, cursor: "pointer", gridColumn: order.status === "in_progress" ? "1/-1" : "auto",
          }}>
            🚨 Sorun Bildir
          </button>
        </div>
      </div>

      {showProblem && (
        <ProblemModal order={order} onClose={() => setShowProblem(false)}
          onSubmit={(type, notes) => {
            doAction("problem", `[${type}] ${notes}`);
            setShowProblem(false);
          }} />
      )}
    </div>
  );
};

// ─── ANA KIOSK SAYFASI ────────────────────────────────────────────────────
export default function Kiosk() {
  const [auth, setAuth] = useState(() => {
    const t = sessionStorage.getItem("kiosk_token");
    const u = sessionStorage.getItem("kiosk_user");
    return t && u ? { token: t, username: u } : null;
  });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchOrders = useCallback(async () => {
    if (!auth) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/kiosk/my-orders`, {
        headers: { Authorization: `Bearer ${auth.token}`, "Content-Type": "application/json" },
      });
      if (res.ok) setOrders(await res.json());
    } catch {}
    setLoading(false);
  }, [auth]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Her 30 saniyede otomatik yenile
  useEffect(() => {
    if (!auth) return;
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, [auth, fetchOrders]);

  const handleLogin = ({ token, username, role }) => {
    sessionStorage.setItem("kiosk_token", token);
    sessionStorage.setItem("kiosk_user", username);
    setAuth({ token, username, role });
  };

  const handleLogout = () => {
    sessionStorage.removeItem("kiosk_token");
    sessionStorage.removeItem("kiosk_user");
    setAuth(null);
    setOrders([]);
    setSelectedOrder(null);
  };

  if (!auth) return <KioskLogin onLogin={handleLogin} />;

  if (selectedOrder) {
    return (
      <OrderDetail
        order={selectedOrder}
        token={auth.token}
        onBack={() => { setSelectedOrder(null); fetchOrders(); }}
        onRefresh={fetchOrders}
      />
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#0f172a", fontFamily: "'DM Sans','Segoe UI',sans-serif", color: "#f1f5f9" }}>

      {/* Header */}
      <div style={{ background: "#1e293b", borderBottom: "1px solid #334155", padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <span style={{ fontSize: 28 }}>🏭</span>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#f1f5f9" }}>Operatör Paneli</div>
            <div style={{ fontSize: 13, color: "#64748b" }}>👤 {auth.username}</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#00ff9d", fontVariantNumeric: "tabular-nums" }}>
              {currentTime.toLocaleTimeString(getLocale(), { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </div>
            <div style={{ fontSize: 12, color: "#64748b" }}>
              {currentTime.toLocaleDateString(getLocale(), { weekday: "long", day: "2-digit", month: "long" })}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={fetchOrders} style={{ padding: "10px 16px", borderRadius: 10, border: "1px solid #334155", background: "none", color: "#64748b", cursor: "pointer", fontSize: 14 }}>🔄</button>
            <button onClick={handleLogout} style={{ padding: "10px 16px", borderRadius: 10, border: "1px solid #f8717144", background: "rgba(248,113,113,0.1)", color: "#f87171", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>Çıkış</button>
          </div>
        </div>
      </div>

      <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>

        {/* Özet */}
        <div style={{ display: "flex", gap: 12, marginBottom: 28 }}>
          {[
            { label: "Toplam İş Emrim", value: orders.length, color: "#60a5fa", icon: "📋" },
            { label: "Üretimde", value: orders.filter(o => o.status === "in_progress").length, color: "#fbbf24", icon: "▶" },
            { label: "Bekliyor", value: orders.filter(o => o.status === "released" || o.status === "draft").length, color: "#a78bfa", icon: "⏳" },
            { label: "Acil", value: orders.filter(o => o.priority === "critical" || o.priority === "high").length, color: "#f87171", icon: "🚨" },
          ].map(s => (
            <div key={s.label} style={{ flex: 1, background: "#1e293b", border: `1px solid ${s.value > 0 && s.label === "Acil" ? "#f87171" : "#334155"}`, borderRadius: 14, padding: "16px 20px", display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 24 }}>{s.icon}</span>
              <div>
                <div style={{ fontSize: 24, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* İş Emirleri */}
        <div style={{ fontSize: 16, fontWeight: 700, color: "#94a3b8", marginBottom: 16, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          📋 İş Emirlerim
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#64748b" }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
            Yükleniyor...
          </div>
        ) : orders.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: "#475569" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#64748b" }}>Atanmış iş emriniz yok</div>
            <div style={{ fontSize: 14, marginTop: 8 }}>Üretim müdürünüzle iletişime geçin</div>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(340px,1fr))", gap: 16 }}>
            {orders.map(order => {
              const pct = order.completion_pct ?? 0;
              const statusCfg = STATUS_CFG[order.status] || STATUS_CFG.released;
              const priorityCfg = PRIORITY_CFG[order.priority] || PRIORITY_CFG.normal;
              const isUrgent = order.priority === "critical" || order.priority === "high";
              return (
                <div key={order.id} onClick={() => setSelectedOrder(order)} style={{
                  background: "#1e293b", border: `2px solid ${isUrgent ? `${priorityCfg.color}55` : "#334155"}`,
                  borderRadius: 16, padding: 20, cursor: "pointer", transition: "all 0.2s",
                  position: "relative", overflow: "hidden",
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = `${statusCfg.color}88`; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = isUrgent ? `${priorityCfg.color}55` : "#334155"; }}>

                  {/* Acil işareti */}
                  {isUrgent && (
                    <div style={{ position: "absolute", top: 0, right: 0, background: priorityCfg.color, color: "#fff", fontSize: 10, fontWeight: 800, padding: "4px 10px", borderRadius: "0 14px 0 10px" }}>
                      {priorityCfg.label.toUpperCase()}
                    </div>
                  )}

                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${statusCfg.color}, transparent)` }} />

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: "#f1f5f9" }}>{order.order_no}</div>
                      <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>{order.product_code}</div>
                    </div>
                    <span style={{ padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700,
                      color: statusCfg.color, background: `${statusCfg.color}18`, border: `1px solid ${statusCfg.color}44` }}>
                      {statusCfg.label}
                    </span>
                  </div>

                  {/* Progress */}
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontSize: 12, color: "#64748b" }}>İlerleme</span>
                      <span style={{ fontSize: 14, fontWeight: 800, color: "#00ff9d" }}>{pct}%</span>
                    </div>
                    <div style={{ height: 8, background: "#0f172a", borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${pct}%`, background: "linear-gradient(90deg,#00ff9d88,#00ff9d)", borderRadius: 4 }} />
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    {[
                      ["Miktar", `${order.quantity} adet`],
                      ["Makine", order.machine || "—"],
                      ["Müşteri", order.customer || "—"],
                      ["Termin", fmt(order.deadline)],
                    ].map(([l, v]) => (
                      <div key={l} style={{ background: "#0f172a", borderRadius: 8, padding: "8px 10px" }}>
                        <div style={{ fontSize: 9, color: "#475569", textTransform: "uppercase", marginBottom: 2 }}>{l}</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#94a3b8" }}>{v}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ marginTop: 14, padding: "12px 16px", borderRadius: 10, textAlign: "center", fontWeight: 700, fontSize: 14,
                    background: order.status === "in_progress" ? "rgba(251,191,36,0.15)" : "rgba(96,165,250,0.15)",
                    color: order.status === "in_progress" ? "#fbbf24" : "#60a5fa",
                    border: `1px solid ${order.status === "in_progress" ? "#fbbf24" : "#60a5fa"}33`,
                  }}>
                    {order.status === "in_progress" ? "▶ Üretim Devam Ediyor — Detay için tıklayın" : "👆 Detay ve İşlemler için tıklayın"}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
